import React, { useState, useMemo } from 'react'
import { X, Search, Clock, TrendingUp, Plus, Trash2, MessageSquare } from 'lucide-react'
import { motion } from 'framer-motion'
import ModalOverlay from '../../../../UI/ModalOverlay/ModalOverlay.tsx'
import { getDesktopSuggestions } from '../../dashboardActionsConfig.tsx'
import { ConversationSession } from '../../../../../utils/conversationStorage.ts'
import styles from './ChatSearchModal.module.scss'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'

interface ChatSearchModalProps {
  isOpen: boolean
  onClose: () => void
  sessions: ConversationSession[]
  currentSessionId: string | null
  onSelectSession: (sessionId: string) => void
  onCreateNewSession: () => void
  onDeleteSession: (sessionId: string) => void
  userId?: string
  userData?: any
}

const ChatSearchModal: React.FC<ChatSearchModalProps> = ({
  isOpen,
  onClose,
  sessions,
  currentSessionId,
  onSelectSession,
  onCreateNewSession,
  onDeleteSession,
  userId,
  userData
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const desktopSuggestions = useMemo(() => {
    return getDesktopSuggestions(navigate, userId, userData, dispatch)
  }, [navigate, userId, userData, dispatch])

  const frequentActions = useMemo(() => {
    const actionCounts = new Map<string, number>()

    sessions.forEach(session => {
      session.messages.forEach(message => {
        if (message.actionId) {
          actionCounts.set(message.actionId, (actionCounts.get(message.actionId) || 0) + 1)
        }
      })
    })

    return Array.from(actionCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([actionId, count]) => {
        const action = desktopSuggestions.find(s => s.id === actionId)
        return action ? { ...action, count } : null
      })
      .filter(Boolean) as Array<typeof desktopSuggestions[0] & { count: number }>
  }, [sessions, desktopSuggestions])

  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) {
      return sessions.slice(0, 10)
    }

    const query = searchQuery.toLowerCase()
    return sessions
      .filter(session => {
        const title = typeof session.title === 'string' ? session.title.toLowerCase() : ''
        if (title.includes(query)) return true
        const firstUserMessage = session.messages.find(m => m.role === 'user')
        if (firstUserMessage) {
          return firstUserMessage.content.toLowerCase().includes(query)
        }
        return false
      })
      .slice(0, 10)
  }, [sessions, searchQuery])

  const handleActionClick = (action: typeof desktopSuggestions[0]) => {
    action.action()
    onClose()
  }

  const handleSessionClick = (sessionId: string) => {
    onSelectSession(sessionId)
    onClose()
  }

  const handleNewSession = () => {
    onCreateNewSession()
    onClose()
  }

  const handleDeleteSession = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation()
    onDeleteSession(sessionId)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Ahora'
    if (diffMins < 60) return `Hace ${diffMins}m`
    if (diffHours < 24) return `Hace ${diffHours}h`
    if (diffDays < 7) return `Hace ${diffDays}d`

    return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })
  }

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: { opacity: 1, scale: 1, y: 0 }
  }

  return (
    <ModalOverlay isOpen={isOpen} onClose={onClose} zIndex={100}>
      <motion.div
        className={styles.chatSearchModal}
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div className={styles.chatSearchHeader}>
          <h2 className={styles.chatSearchTitle}>Mis chats</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              className={styles.chatSearchNewButton}
              onClick={handleNewSession}
              type="button"
              aria-label="Nueva conversación"
            >
              <Plus size={18} />
              <span>Nueva conversación</span>
            </button>
            <button
              className={styles.chatSearchCloseButton}
              onClick={onClose}
              type="button"
              aria-label="Cerrar"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className={styles.chatSearchInputContainer}>
          <Search size={20} className={styles.chatSearchInputIcon} />
          <input
            type="text"
            className={styles.chatSearchInput}
            placeholder="Buscar en tus conversaciones..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className={styles.chatSearchContent}>
          {frequentActions.length > 0 && (
            <div className={styles.chatSearchSection}>
              <div className={styles.chatSearchSectionHeader}>
                <TrendingUp size={18} />
                <span className={styles.chatSearchSectionTitle}>Acciones frecuentes</span>
              </div>
              <div className={styles.frequentActionsList}>
                {frequentActions.map((action) => (
                  <button
                    key={action.id}
                    className={styles.frequentActionItem}
                    onClick={() => handleActionClick(action)}
                    type="button"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className={styles.chatSearchSection}>
            <div className={styles.chatSearchSectionHeader}>
              <Clock size={18} />
              <span className={styles.chatSearchSectionTitle}>
                {searchQuery ? 'Resultados de búsqueda' : 'Conversaciones recientes'}
              </span>
            </div>
            {filteredSessions.length === 0 ? (
              <div className={styles.chatSearchEmpty}>
                <MessageSquare size={32} style={{ opacity: 0.3, marginBottom: '12px' }} />
                <p>No se encontraron conversaciones</p>
              </div>
            ) : (
              <div className={styles.chatSearchSessionsList}>
                {filteredSessions.map((session) => {
                  const firstUserMessage = session.messages.find(m => m.role === 'user')
                  const fromTitle =
                    typeof session.title === 'string' && session.title.trim().length > 0
                      ? session.title.trim()
                      : ''
                  const preview = fromTitle
                    ? fromTitle.length > 60
                      ? fromTitle.substring(0, 60) + '...'
                      : fromTitle
                    : firstUserMessage
                      ? firstUserMessage.content.length > 60
                        ? firstUserMessage.content.substring(0, 60) + '...'
                        : firstUserMessage.content
                      : 'Nueva conversación'

                  return (
                    <div
                      key={session.sessionId}
                      className={`${styles.chatSearchSessionItem} ${
                        session.sessionId === currentSessionId ? styles.chatSearchSessionItemActive : ''
                      }`}
                      onClick={() => handleSessionClick(session.sessionId)}
                    >
                      <div className={styles.chatSearchSessionContent}>
                        <MessageSquare size={16} style={{ opacity: 0.6, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className={styles.chatSearchSessionPreview}>{preview}</span>
                            {session.sessionId === currentSessionId && (
                              <span className={styles.chatSearchCurrentBadge}>Actual</span>
                            )}
                          </div>
                          <span className={styles.chatSearchSessionMeta}>
                            {formatDate(session.createdAt)} · {session.messageCount} mensajes
                          </span>
                        </div>
                      </div>
                      <button
                        className={styles.chatSearchDeleteButton}
                        onClick={(e) => handleDeleteSession(e, session.sessionId)}
                        type="button"
                        aria-label="Eliminar conversación"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </ModalOverlay>
  )
}

export default ChatSearchModal
